import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Combobox } from "@/components/ui/combobox";
import { DatePicker } from "@/components/ui/date-picker";
import { MarkdownEditor } from "@/components/ui/markdown-editor";
import { Select } from "@/components/ui/select";
import {
  SimilarWorkItemsWarning,
  useSimilarWorkItems,
} from "@/components/work-items/similar-warning";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { useDraft } from "@/lib/use-draft";
import { Link, createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

/**
 * `/work-items/new/:type` — the global "New <type>" form. `type`
 * is a required path segment (feature / bug / standalone / hotfix) —
 * picking the URL is how you choose the workflow.
 *
 * Form differences per type:
 *   - feature: full form (initial state, priority, assignee, release,
 *     estimate, due date).
 *   - bug: same minus the initial state (no estimation phase).
 *   - standalone: same minus release and initial state.
 *   - hotfix: priority + assignee + title + description only;
 *     the due date is auto-computed from priority × project SLA.
 *     Reserve it for a fix that must ship immediately, bypassing
 *     the normal dev/test chain — genuinely rare by design.
 */

const TYPE_IDS = ["feature", "bug", "standalone", "hotfix"] as const;

type WorkItemType = (typeof TYPE_IDS)[number];

const searchSchema = z.object({ project: z.string().optional() });

type NewFormData = {
  projects: { id: string; name: string; slug: string }[];
  projectId: string | null;
  assigneeCandidates: { id: string; name: string | null }[];
  releases: { id: string; name: string }[];
  sla: { urgent: string; high: string; normal: string; low: string } | null;
};

export const Route = createFileRoute("/_app/work-items/new/$type")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ project: search.project }),
  loader: async ({ params, deps }) => {
    if (!(TYPE_IDS as readonly string[]).includes(params.type)) throw notFound();
    const qs = deps.project ? `?project=${encodeURIComponent(deps.project)}` : "";
    return api.get<NewFormData>(`/api/work-items/new-form${qs}`);
  },
  component: NewWorkItemPage,
});

const INPUT_CLS =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
const LABEL_CLS = "text-sm font-medium leading-none block mb-1.5";

function NewWorkItemPage() {
  const { type } = Route.useParams();
  const issueType = type as WorkItemType;
  const { t } = useTranslation();
  const tk = `wiNew.types.${issueType}` as const;
  const isHotfix = issueType === "hotfix";
  // Bug takes a release like a feature but has no estimation phase, so
  // only a feature gets the initial-state shortcut.
  const isFeature = issueType === "feature";
  const showRelease = issueType === "feature" || issueType === "bug";

  const { projects, projectId, assigneeCandidates, releases, sla } = Route.useLoaderData();
  const navigate = useNavigate();
  const create = useApiMutation(
    (body: Record<string, unknown>) => api.post<{ globalId: number }>("/api/work-items", body),
    {
      onSuccess: (result) => {
        setTitle("");
        setDescription("");
        return navigate({ to: "/work-item/$id", params: { id: String(result.globalId) } });
      },
      errorMessage: (err) =>
        err instanceof ApiError ? t("wiNew.createFailedApi") : t("wiNew.createFailed"),
    },
  );
  const [description, setDescription] = useDraft(`new-wi-page:${projectId ?? "any"}:description`);
  const [title, setTitle] = useDraft(`new-wi-page:${projectId ?? "any"}:title`);

  // Warn about likely-duplicate open items in the picked project as the title
  // takes shape — the same check as the New-WI dialog and the AI review card.
  const similar = useSimilarWorkItems(projectId ?? "", title, description);

  function onProjectChange(value: string) {
    const picked = projects.find((p) => p.id === value);
    navigate({
      to: "/work-items/new/$type",
      params: { type },
      search: picked ? { project: picked.slug } : {},
    });
  }

  async function createWorkItem(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const selectedProjectId = (fd.get("projectId") as string) ?? "";
    const title = ((fd.get("title") as string) ?? "").trim();
    if (!selectedProjectId || !title) {
      create.setError(t("wiNew.requiredError"));
      return;
    }
    const estimatedRaw = ((fd.get("estimatedHours") as string) ?? "").trim();
    const dueDateRaw = ((fd.get("dueDate") as string) ?? "").trim();
    await create.mutate({
      type: issueType,
      projectId: selectedProjectId,
      title,
      description: description.trim() || null,
      priority: (fd.get("priority") as string) || "normal",
      assigneeId: (fd.get("assigneeId") as string) || null,
      releaseId: showRelease ? (fd.get("releaseId") as string) || null : null,
      estimatedHours: !isHotfix && estimatedRaw ? Number.parseFloat(estimatedRaw) : null,
      dueDate: !isHotfix && dueDateRaw ? dueDateRaw : null,
      ...(isFeature ? { initialStateName: (fd.get("initialState") as string) || "new" } : {}),
    });
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold">{t(`${tk}.new`)}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t(`${tk}.hint`)}</p>
        </div>
        <Button asChild variant="outline">
          <Link to="/work-items">{t("wiNew.cancel")}</Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t(`${tk}.details`)}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={createWorkItem} className="space-y-5">
            <div className="block">
              <span className={LABEL_CLS}>
                {t("wiNew.project")} <span className="text-destructive">*</span>
              </span>
              <Combobox
                name="projectId"
                value={projectId ?? ""}
                onChange={onProjectChange}
                placeholder={t("wiNew.selectProject")}
                emptyLabel={t("wiNew.noProjects")}
                options={projects.map((p) => ({ value: p.id, label: p.name }))}
              />
            </div>

            {isFeature && (
              <div className="block">
                <span className={LABEL_CLS}>{t("wiNew.initialState")}</span>
                <Select
                  aria-label={t("wiNew.initialState")}
                  name="initialState"
                  defaultValue="new"
                  options={[
                    { value: "new", label: t("wiNew.stateNew") },
                    { value: "estimation", label: t("wiNew.stateEstimation") },
                    { value: "approving", label: t("wiNew.stateApproving") },
                  ]}
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="block">
                <span className={LABEL_CLS}>{t("wiNew.priority")}</span>
                <Select
                  aria-label={t("wiNew.priority")}
                  name="priority"
                  defaultValue={isHotfix ? "urgent" : "normal"}
                  options={[
                    { value: "low", label: t("wiNew.priorities.low") },
                    { value: "normal", label: t("wiNew.priorities.normal") },
                    { value: "high", label: t("wiNew.priorities.high") },
                    { value: "urgent", label: t("wiNew.priorities.urgent") },
                  ]}
                />
                {isHotfix && sla && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {t("wiNew.sla", {
                      urgent: sla.urgent,
                      high: sla.high,
                      normal: sla.normal,
                      low: sla.low,
                    })}
                  </p>
                )}
              </div>
              <div className="block">
                <span className={LABEL_CLS}>{t("wiNew.assignee")}</span>
                <Combobox
                  name="assigneeId"
                  defaultValue=""
                  disabled={!projectId}
                  placeholder={projectId ? t("wiNew.defaultTeam") : t("wiNew.selectProjectFirst")}
                  emptyLabel={t("wiNew.noMembers")}
                  clearable
                  options={assigneeCandidates.map((u) => ({
                    value: u.id,
                    label: u.name ?? u.id,
                  }))}
                />
              </div>
            </div>

            {showRelease && (
              <div className="block">
                <span className={LABEL_CLS}>{t("wiNew.targetRelease")}</span>
                <Combobox
                  name="releaseId"
                  defaultValue=""
                  placeholder={t("wiNew.none")}
                  emptyLabel={t("wiNew.noReleases")}
                  clearable
                  options={releases.map((v) => ({ value: v.id, label: v.name }))}
                />
              </div>
            )}

            <label className="block">
              <span className={LABEL_CLS}>
                {t("wiNew.title")} <span className="text-destructive">*</span>
              </span>
              <input
                type="text"
                name="title"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t(`${tk}.titlePlaceholder`)}
                className={INPUT_CLS}
              />
            </label>

            <SimilarWorkItemsWarning matches={similar} />

            <div className="block">
              <span className={LABEL_CLS}>{t("wiNew.description")}</span>
              <MarkdownEditor
                value={description}
                onChange={setDescription}
                rows={6}
                autoGrow
                placeholder={t(`${tk}.describe`)}
                // Upload to the picked project's GitLab store. Hub
                // stores no local copy of the file. Until a project
                // is picked, the editor renders without an upload
                // toolbar.
                uploadEndpoint={projectId ? `/api/projects/${projectId}/uploads` : undefined}
              />
            </div>

            {!isHotfix && (
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className={LABEL_CLS}>{t("wiNew.estimatedHours")}</span>
                  <input
                    type="number"
                    name="estimatedHours"
                    step="0.25"
                    min="0"
                    placeholder="0.00"
                    className={INPUT_CLS}
                  />
                </label>
                <label htmlFor="wi-due-date" className="block">
                  <span className={LABEL_CLS}>{t("wiNew.dueDate")}</span>
                  <DatePicker id="wi-due-date" name="dueDate" />
                </label>
              </div>
            )}

            {create.error && <p className="text-sm text-destructive">{create.error}</p>}

            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={create.pending}>
                {create.pending ? t("wiNew.creating") : t(`${tk}.create`)}
              </Button>
              <Button type="button" variant="outline" asChild>
                <Link to="/work-items">{t("wiNew.cancel")}</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
